import { __assign, __spreadArray } from 'tslib';
import { isGroup } from '../../../select/esm/shared';
import { maskUtils, getDataTestId } from '../../../shared/esm';
import { DEFAULT_PHONE_FORMAT } from '../consts.js';
import { countriesData } from '../data/country-data.js';

function initCountries(iso2s, customCountriesList) {
    var data = customCountriesList !== null && customCountriesList !== void 0 ? customCountriesList : countriesData;
    var filteredCountriesData = Array.isArray(iso2s)
        ? data.filter(function (country) { return iso2s.includes(country[2]); })
        : data;
    return filteredCountriesData.map(function (country) {
        var _a;
        var countryItem = {
            name: country[0],
            regions: country[1],
            iso2: country[2],
            countryCode: country[3],
            dialCode: country[3],
            format: country[4],
            priority: country[5] || 0,
        };
        var areaItems = [];
        if (country[6]) {
            (_a = country[6]) === null || _a === void 0 ? void 0 : _a.forEach(function (areaCode) {
                var areaItem = __assign({}, countryItem);
                areaItem.dialCode = country[3] + areaCode;
                areaItem.isAreaCode = true;
                areaItem.areaCodeLength = areaCode.length;
                areaItems.push(areaItem);
            });
        }
        if (areaItems.length > 0) {
            countryItem.mainCode = true;
            return __spreadArray([countryItem], areaItems, true);
        }
        return [countryItem];
    });
}
function findCountry(countries, value, iso2, country) {
    var _a;
    if (country)
        return country;
    if (value) {
        var inputNumber = clearMask(value);
        var nextCountry = guessCountry(inputNumber, countries);
        return nextCountry;
    }
    if (!iso2)
        return undefined;
    return (_a = countries.find(function (c) { return c[0].iso2 === iso2; })) === null || _a === void 0 ? void 0 : _a[0];
}
function guessCountry(inputNumber, data) {
    var _a;
    var inputNumberDialCode = inputNumber.slice(0, 6);
    var result = data.reduce(function (selectedCountry, countryData) {
        var guess;
        countryData.forEach(function (country) {
            if (inputNumberDialCode.startsWith(country.dialCode)) {
                var selectedCountryDialCode = (selectedCountry === null || selectedCountry === void 0 ? void 0 : selectedCountry.dialCode) || '';
                var selectedCountryPriority = (selectedCountry === null || selectedCountry === void 0 ? void 0 : selectedCountry.priority) || 100;
                if (country.dialCode.length > selectedCountryDialCode.length) {
                    guess = country;
                }
                if (country.dialCode.length === selectedCountryDialCode.length &&
                    country.priority < selectedCountryPriority) {
                    guess = country;
                }
            }
        });
        return guess || selectedCountry;
    }, undefined);
    if (!result && inputNumber.length > 5 && inputNumber.startsWith('8'))
        return (_a = data.find(function (countryData) { return countryData[0].iso2 === 'ru'; })) === null || _a === void 0 ? void 0 : _a[0];
    return result;
}
function clearMask(value) {
    return value.replace(/\D/g, '');
}
function itemToMask(item) {
    return item === '.' ? /\d/ : item;
}
function prepareDefaultFormat(countryCode, clearableCountryCode) {
    if (!countryCode)
        return __spreadArray(['+'], DEFAULT_PHONE_FORMAT.split('').map(itemToMask), true);
    var removeDots = function (count) {
        var removed = 0;
        var res = DEFAULT_PHONE_FORMAT;
        while (removed < count) {
            res = res.replace(/\./, '');
            removed += 1;
        }
        return res.trim();
    };
    if (clearableCountryCode) {
        return __spreadArray(__spreadArray(__spreadArray([
            '+'
        ], countryCode.split('').map(function () { return /\d/; }), true), [
            ' '
        ], false), removeDots(countryCode.length).split('').map(itemToMask).reverse(), true);
    }
    return __spreadArray(__spreadArray(__spreadArray([
        '+'
    ], countryCode.split(''), true), [
        ' '
    ], false), removeDots(countryCode.length).split('').map(itemToMask).reverse(), true);
}
function createPhoneMaskExpression(country, clearableCountryCode) {
    var _a = country || {}, countryCode = _a.countryCode, format = _a.format;
    if (!countryCode || !format) {
        return prepareDefaultFormat(countryCode, clearableCountryCode);
    }
    return __spreadArray(__spreadArray(__spreadArray([
        '+'
    ], (clearableCountryCode ? countryCode.split('').map(function () { return /\d/; }) : countryCode.split('')), true), [
        ' '
    ], false), format.split('').map(itemToMask), true);
}
function createMaskOptions(country, clearableCountryCode, preserveCountryCode, lastCountryRef) {
    var countryCode = country === null || country === void 0 ? void 0 : country.countryCode;
    var prefix = countryCode ? getInitialValueFromCountry(countryCode) : '';
    var prefixLen = !clearableCountryCode && prefix ? prefix.length : 0;
    var mask = createPhoneMaskExpression(country, clearableCountryCode);
    var preprocessors = [
        maskUtils.insertionPhonePreprocessor(mask, countryCode, clearableCountryCode),
    ];
    if (preserveCountryCode) {
        var createMask = function (lastCountry) {
            return createPhoneMaskExpression(lastCountry, clearableCountryCode);
        };
        preprocessors.push(maskUtils.preserveCountryCodePreprocessor(lastCountryRef, createMask));
    }
    return {
        mask: mask,
        preprocessors: preprocessors,
        postprocessors: [maskUtils.prefixPostprocessor(prefixLen > 0 ? prefix : '')],
        plugins: [
            maskUtils.caretGuard(function (value, _a) {
                var from = _a[0], to = _a[1];
                return [
                    from === to ? prefixLen : 0,
                    value.length,
                ];
            }),
        ],
    };
}
function defaultFilterFn(value, option) {
    if (value === void 0) { value = ''; }
    return clearMask(option.key).includes(clearMask(value));
}
var EMPTY_OPTIONS = [];
var filterPhones = function (value, options, filterFn) {
    if (value === void 0) { value = ''; }
    if (filterFn === void 0) { filterFn = defaultFilterFn; }
    if (!options || options.length === 0)
        return EMPTY_OPTIONS;
    var filteredOptions = [];
    options.forEach(function (option) {
        if (isGroup(option)) {
            var group_1 = __assign(__assign({}, option), { options: [] });
            option.options.forEach(function (groupOption) {
                if (filterFn(value, groupOption))
                    group_1.options.push(groupOption);
            });
            if (group_1.options.length)
                filteredOptions.push(group_1);
        }
        else if (filterFn(value, option))
            filteredOptions.push(option);
    });
    return filteredOptions;
};
function getPhoneData(phone, countries, defaultIso2) {
    var nextPhone = phone;
    var inputNumber = clearMask(nextPhone);
    var nextCountry = findCountry(countries, phone, defaultIso2);
    if ((nextCountry === null || nextCountry === void 0 ? void 0 : nextCountry.iso2) === 'ru' && inputNumber.length > 5 && inputNumber.startsWith('8')) {
        nextPhone = phone.replace(/\+?8/, '+7');
    }
    return {
        nextPhone: nextPhone,
        nextCountry: nextCountry,
    };
}
function getClear(clear, clearableCountryCode, value, countryCode) {
    if (value === void 0) { value = ''; }
    if (countryCode === void 0) { countryCode = ''; }
    if (!clear)
        return false;
    var trimmedValue = value.trim();
    return clearableCountryCode
        ? trimmedValue !== '' && trimmedValue !== '+'
        : trimmedValue.length > countryCode.length + 1;
}
function getInternationalPhoneInputDesktopTestIds(dataTestId) {
    return {
        fieldAutocompleteWrapper: dataTestId,
        fieldAutocompleteInner: "".concat(dataTestId, "-field-form-control-inner"),
        fieldAutocompleteFormControl: "".concat(dataTestId, "-field-form-control"),
        fieldAutocompleteLeftAddons: "".concat(dataTestId, "-field-form-control-left-addons"),
        fieldAutocompleteRightAddons: "".concat(dataTestId, "-field-form-control-right-addons"),
        fieldAutocompleteError: "".concat(dataTestId, "-field-form-control-error-message"),
        fieldAutocompleteHint: "".concat(dataTestId, "-field-form-control-hint"),
        option: getDataTestId(dataTestId, 'option'),
        optionsList: getDataTestId(dataTestId, 'options-list'),
        countryOption: getDataTestId(dataTestId, 'country-select-option'),
        countryOptionsList: getDataTestId(dataTestId, 'country-select-options-list'),
        field: dataTestId,
        fieldInner: getDataTestId(dataTestId, 'form-control-inner'),
        fieldFormControl: getDataTestId(dataTestId, 'form-control'),
        fieldLeftAddons: getDataTestId(dataTestId, 'form-control-left-addons'),
        fieldRightAddons: getDataTestId(dataTestId, 'form-control-right-addons'),
        fieldError: getDataTestId(dataTestId, 'form-control-error-message'),
        fieldHint: getDataTestId(dataTestId, 'form-control-hint'),
    };
}
function getInternationalPhoneInputMobileTestIds(dataTestId) {
    return {
        fieldAutocompleteWrapper: dataTestId,
        fieldAutocompleteInner: getDataTestId(dataTestId, 'field-form-control-inner'),
        fieldAutocompleteFormControl: getDataTestId(dataTestId, 'field-form-control'),
        fieldAutocompleteLeftAddons: getDataTestId(dataTestId, 'field-form-control-left-addons'),
        fieldAutocompleteRightAddons: getDataTestId(dataTestId, 'field-form-control-right-addons'),
        fieldAutocompleteError: getDataTestId(dataTestId, 'field-form-control-error-message'),
        fieldAutocompleteHint: getDataTestId(dataTestId, 'field-form-control-hint'),
        option: getDataTestId(dataTestId, 'option'),
        optionsList: getDataTestId(dataTestId, 'options-list'),
        bottomSheet: getDataTestId(dataTestId, 'bottom-sheet'),
        bottomSheetHeader: getDataTestId(dataTestId, 'bottom-sheet-header'),
        bottomSheetContent: getDataTestId(dataTestId, 'bottom-sheet-content'),
        countryOption: getDataTestId(dataTestId, 'country-select-option'),
        countryOptionsList: getDataTestId(dataTestId, 'country-select-options-list'),
        countryBottomSheet: getDataTestId(dataTestId, 'country-select-bottom-sheet'),
        countryBottomSheetHeader: getDataTestId(dataTestId, 'country-select-bottom-sheet-header'),
        countryBottomSheetContent: getDataTestId(dataTestId, 'country-select-bottom-sheet-content'),
        clearButton: getDataTestId(dataTestId, 'clear'),
        applyButton: getDataTestId(dataTestId, 'apply'),
        field: dataTestId,
        fieldInner: getDataTestId(dataTestId, 'form-control-inner'),
        fieldFormControl: getDataTestId(dataTestId, 'form-control'),
        fieldLeftAddons: getDataTestId(dataTestId, 'form-control-left-addons'),
        fieldRightAddons: getDataTestId(dataTestId, 'form-control-right-addons'),
        fieldError: getDataTestId(dataTestId, 'form-control-error-message'),
        fieldHint: getDataTestId(dataTestId, 'form-control-hint'),
        searchInput: getDataTestId(dataTestId, 'search'),
        searchFormControl: getDataTestId(dataTestId, 'search-form-control'),
        searchInner: getDataTestId(dataTestId, 'search-form-control-inner'),
        searchLeftAddons: getDataTestId(dataTestId, 'search-form-control-left-addons'),
        searchRightAddons: getDataTestId(dataTestId, 'search-form-control-right-addons'),
        searchError: getDataTestId(dataTestId, 'search-form-control-error-message'),
        searchHint: getDataTestId(dataTestId, 'search-form-control-hint'),
    };
}
function getInitialValueFromCountry(countryCode) {
    return "+".concat(countryCode);
}

export { clearMask, createMaskOptions, createPhoneMaskExpression, filterPhones, findCountry, getClear, getInitialValueFromCountry, getInternationalPhoneInputDesktopTestIds, getInternationalPhoneInputMobileTestIds, getPhoneData, guessCountry, initCountries };
